import { FormikProps } from 'formik';
import { Dispatch } from 'redux';
import { Col, Input, Label, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { useTranslation } from 'react-i18next';

import { useCallback, useEffect, useMemo, useState } from 'react';
import FluidContainerClean from 'Components/pos2u/Wrappers/FluidContainer';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import {
  setCurrentPage,
  setEntriesPerPage,
  setInputPageValue,
} from 'slices/productInventory/reducer';

import InventoryHistoryTable from './InventoryHistoryTable';
import { getInventoryHistoryList } from 'services/ProductInventoryService';
import {
  EditInventoryDetailsForm,
  InventoryHistory,
} from 'types/product/productInventoryTypes';
import { mapInventoryHistoryData } from 'utils/mappers/InventoryHistoryMapper';
import { LoadStatus } from 'types/common/apiStatus';

const columnWhiteSpace = 'normal';

const InventoryDetailsForm = ({
  dispatch,
  formik,
}: {
  dispatch: Dispatch;
  formik: FormikProps<EditInventoryDetailsForm>;
}) => {
  const { t } = useTranslation();
  const layoutState = useSelector((state: RootState) => state.Layout);
  const { layoutModeType } = layoutState;
  const isDarkMode = layoutModeType === 'dark';

  const productInventoryState = useSelector(
    (state: RootState) => state.ProductInventory
  );
  const { InventoryDetailsModal } = productInventoryState;
  const { PageDetails, activeInventoryDetailsIndex } = InventoryDetailsModal;

  const columnHelper = createColumnHelper<InventoryHistory>();

  const [InventoryHistoryList, setInventoryHistory] = useState<
    InventoryHistory[]
  >([]);
  const [InventoryHistoryTotal, setInventoryHistoryTotal] = useState<number>(0);
  const [historyStatus, setHistoryStatus] = useState<LoadStatus>('idle');

  const fetchInventoryHistoryData = useCallback(async () => {
    if (!activeInventoryDetailsIndex) return;

    if (historyStatus !== 'idle') return;

    setHistoryStatus('loading');

    try {
      const rs = await getInventoryHistoryList(
        activeInventoryDetailsIndex,
        PageDetails.currentPage + 1
      );
      const { message, error_code, data } = rs;
      if (message === 'OK' && error_code === 0) {
        const { total, inventory_history } = data;
        setInventoryHistoryTotal(total);
        const mapped = mapInventoryHistoryData(inventory_history);
        setInventoryHistory(mapped);
        setHistoryStatus('success');
      } else {
        setInventoryHistoryTotal(0);
        setInventoryHistory([]);
        setHistoryStatus('error');
      }
    } catch (error) {
      console.error('Failed to fetch Inventory History data:', error);
      setHistoryStatus('error');
    }
  }, [activeInventoryDetailsIndex, PageDetails.currentPage, historyStatus]);

  useEffect(() => {
    fetchInventoryHistoryData();
  }, [fetchInventoryHistoryData]);

  const InventoryHistoryColumns: ColumnDef<InventoryHistory, any>[] = useMemo(
    () => [
      columnHelper.display({
        id: `date`,
        header: () => <div className="text-center">{t('Date')}</div>,
        cell: (cell) => (
          <div id={`date-${cell.row.original.id}`}>
            <span>{cell.row.original.date}</span>
          </div>
        ),
        meta: {},

        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: `userLogin`,
        header: () => <div className="text-center">{t('User Login')}</div>,

        cell: (cell) => (
          <div id={`userLogin-${cell.row.original.id}`}>
            <span>{cell.row.original.userLogin}</span>
          </div>
        ),
        meta: {},
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: `quantity`,
        header: () => <div className="text-center">{t('Quantity')}</div>,

        cell: (cell) => (
          <div
            id={`quantity-${cell.row.original.id}`}
            className={`text-center`}
          >
            <span>{cell.row.original.quantity}</span>
          </div>
        ),
        meta: {},
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: `comment`,
        header: () => <div className="text-center">{t('INV_Comments')}</div>,

        cell: (cell) => (
          <div id={`comment-${cell.row.original.id}`}>
            <span>{cell.row.original.comment}</span>
          </div>
        ),
        meta: {},
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [t, columnHelper]
  );

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
  };
  const totalPages = Math.ceil(
    InventoryHistoryTotal / PageDetails.entriesPerPage
  );
  return (
    <>
      <div className={`mb-lg-3 mb-xs-1 row`}>
        <Label htmlFor="#" className="col-sm-4 col-form-label">
          {t('Item ID')}
        </Label>
        <Col sm={8} className="d-flex align-items-center">
          <FluidContainerClean>
            <Input
              type="text"
              name={`itemIdNumber`}
              value={formik.values.itemIdNumber || ''}
              onChange={formik.handleChange}
              className="me-2"
              disabled
            />
          </FluidContainerClean>
        </Col>
      </div>
      <div className={`mb-lg-3 mb-xs-1 row`}>
        <Label htmlFor="#" className="col-sm-4 col-form-label">
          {t('Barcode ID')}
        </Label>
        <Col sm={8} className="d-flex align-items-center">
          <FluidContainerClean>
            <Input
              type="text"
              name={`barcode`}
              value={formik.values.barcode || ''}
              onChange={formik.handleChange}
              className="me-2"
              disabled
            />
          </FluidContainerClean>
        </Col>
      </div>
      <div className={`mb-lg-3 mb-xs-1 row`}>
        <Label htmlFor="#" className="col-sm-4 col-form-label">
          {t('Item Name')}
        </Label>
        <Col sm={8} className="d-flex align-items-center">
          <FluidContainerClean>
            <Input
              type="text"
              name={`name`}
              value={formik.values.name || ''}
              onChange={formik.handleChange}
              className="me-2"
              disabled
            />
          </FluidContainerClean>
        </Col>
      </div>

      <div className={`mb-lg-3 mb-xs-1 row`}>
        <Label htmlFor="#" className="col-sm-4 col-form-label">
          {t('Quantity Balance')}
        </Label>
        <Col sm={8} className="d-flex align-items-center">
          <FluidContainerClean>
            <Input
              type="text"
              name={`quantityBalance`}
              value={formik.values.quantityBalance || ''}
              onChange={formik.handleChange}
              className="me-2"
              disabled
            />
          </FluidContainerClean>
        </Col>
      </div>
      <div className={``}>
        <InventoryHistoryTable
          columns={InventoryHistoryColumns}
          data={InventoryHistoryList || []}
          customPageSize={PageDetails.entriesPerPage}
          divClass="table-responsive table-card mb-1 mt-0 mx-auto"
          tableClass="align-middle table-nowrap"
          theadClass={`table-${
            isDarkMode ? 'dark' : 'light'
          } text-muted text-uppercase`}
          thClass={'px-1'}
          tdClass={'px-1'}
          TotalData={InventoryHistoryTotal}
          totalPages={totalPages}
          setEntriesPerPage={setEntriesPerPage}
          currentPage={PageDetails.currentPage}
          handlePageChange={handlePageChange}
          inputPageValue={PageDetails.inputPageValue}
          setInputPageValue={setInputPageValue}
          columnWhiteSpace={columnWhiteSpace}
        />
      </div>

      <Row>
        <Col xs={12} sm={12} className="text-center">
          <div className="mb-lg-3 mb-xs-1">
            <Row>
              <Col xs={0} sm={4}></Col>

              <Col xs={5} sm={3}>
                {/* <Button
                  block
                  color="success"
                  type="submit"
                  id="edit-price-save"
                  disabled={
                    InventoryDetailsModal.FormErrorCount !== 0 ||
                    formik.isSubmitting
                  }
                  // disabled={formik.isSubmitting}
                  onClick={async () => {
                    await formik.setTouched(
                      Object.keys(formik.initialValues).reduce(
                        (acc, key) => ({ ...acc, [key]: true }),
                        {}
                      )
                    );
                    dispatch(setShowAllValidatePriceErrors(true));
                    dispatch(setCountPriceSubmit());
                  }}
                >
                  {t('Btn_Save')}
                </Button> */}
              </Col>
              {/* <Col xs={1} sm={3}></Col> */}
            </Row>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default InventoryDetailsForm;
